import { createSelector } from '@reduxjs/toolkit';

import type { CaptionLine, CaptionRequests } from './callCaptionsSlice';

import type { RootState } from 'features/app/store/store';
import { normalizePhoneNumber } from 'features/call/call-base/helpers';
import {
  getCaptionDisplayConfigFlag,
  getCaptionTurnBuildOptions,
  resolveCaptionDisplayConfig,
  type CaptionDisplayConfig,
} from 'features/call/call-captions/constants';
import {
  buildCaptionSpeakerColorMap,
  buildCaptionTurns,
  resolveCaptionSpeakerColorVariant,
  resolveCaptionSpeakerLabel,
} from 'features/call/call-captions/helpers';
import {
  hearingsSelector,
  primaryHearingSelector,
} from 'features/call/call-hearing/store';
import { selectIsAstSignMailHandoffActive } from 'features/ast-signmail/store';
import { FeatureFlagService } from 'features/feature-toggle/services';
import { selectIsSignMailSessionInProgress } from 'features/signmail/store';

const UNKNOWN_HEARING_NAME_LABELS = new Set([
  'unknown hearing person',
  'unknownhearingname',
]);

export const selectRawCaptionLines = ({
  callCaptions,
}: RootState): CaptionLine[] => callCaptions.lines;

export const getVisibleCaptions = (
  captionLines: CaptionLine[]
): CaptionLine[] => {
  const partialCaptions = captionLines.filter((caption) => caption.isPartial);
  const finalCaptions = captionLines.filter((caption) => !caption.isPartial);

  const getSortKey = (caption: CaptionLine) =>
    caption.startedAtMs ?? caption.receivedAtMs;

  const sortedFinalCaptions = finalCaptions
    .sort((left, right) => getSortKey(left) - getSortKey(right))
    .slice();
  const latestPartialCaption = partialCaptions
    .toSorted((left, right) => getSortKey(left) - getSortKey(right))
    .slice(-1);

  return [...sortedFinalCaptions, ...latestPartialCaption];
};

/** Visible lines after partial dedup — same output WSBridge relied on before raw storage. */
export const selectCallCaptionLines = createSelector(
  [selectRawCaptionLines],
  getVisibleCaptions
);

export const selectCaptionsEnabled = ({ callCaptions }: RootState) =>
  callCaptions.captionsEnabled;

const defaultCaptionRequests: CaptionRequests = {
  localInterpreter: false,
  remoteInterpreter: false,
  deafEndpoint: false,
};

// Back-compat: older persisted/in-flight state may lack captionRequests; treat
// localInterpreter as captionsEnabled in that case.
// Memoized so downstream `createSelector` consumers keep a stable reference
// when neither the underlying requests object nor captionsEnabled changes.
export const selectCaptionRequests = createSelector(
  [
    ({ callCaptions }: RootState) => callCaptions.captionRequests,
    selectCaptionsEnabled,
  ],
  (captionRequests, captionsEnabled): CaptionRequests => ({
    ...defaultCaptionRequests,
    localInterpreter: captionRequests?.localInterpreter ?? captionsEnabled,
    remoteInterpreter: captionRequests?.remoteInterpreter ?? false,
    deafEndpoint: captionRequests?.deafEndpoint ?? false,
  })
);

export const selectLocalInterpreterRequestsCaptions = createSelector(
  [selectCaptionRequests],
  (captionRequests) => captionRequests.localInterpreter
);

export const selectRemoteInterpreterRequestsCaptions = createSelector(
  [selectCaptionRequests],
  (captionRequests) => captionRequests.remoteInterpreter
);

export const selectDeafEndpointRequestsCaptions = createSelector(
  [selectCaptionRequests],
  (captionRequests) => captionRequests.deafEndpoint
);

export const selectAnyParticipantRequestsCaptions = createSelector(
  [selectCaptionRequests],
  (captionRequests) =>
    captionRequests.localInterpreter ||
    captionRequests.remoteInterpreter ||
    captionRequests.deafEndpoint
);

/**
 * Derived block: true during SignMail placement (gated by disableDuringSignmail
 * LD flag) or AST handoff (unconditional).
 */
export const selectAreCaptionsBlockedBySignMail = (
  state: RootState
): boolean => {
  if (selectIsAstSignMailHandoffActive(state)) return true;
  if (!selectIsSignMailSessionInProgress(state)) return false;
  const config = resolveCaptionDisplayConfig(
    getCaptionDisplayConfigFlag(FeatureFlagService.getInstance().flags)
  );
  return config.disableDuringSignmail;
};

/** Any requester (including deafEndpoint) → keep the shared ASR session up. */
export const selectShouldRunCaptionsSession = createSelector(
  [selectAnyParticipantRequestsCaptions, selectAreCaptionsBlockedBySignMail],
  (anyRequests, blocked) => anyRequests && !blocked
);

/**
 * VI display buffer and/or deaf WSBridge relay. Intentionally excludes
 * deafEndpoint until that intent path owns forwarding (see WSBridge TODO).
 * Distinct from captionsEnabled: local UI can be off while we still ingest for
 * peer/deaf relay.
 */
export const selectShouldIngestCaptions = createSelector(
  [selectCaptionRequests, selectAreCaptionsBlockedBySignMail],
  (captionRequests, blocked) =>
    !blocked &&
    (captionRequests.localInterpreter || captionRequests.remoteInterpreter)
);

export const selectCaptionTrimOffsets = ({ callCaptions }: RootState) =>
  callCaptions.trimOffsets ?? {};

export const selectCaptionsRegionActive = createSelector(
  [selectCallCaptionLines],
  (lines) => lines.length > 0
);

export const selectShouldShowCaptionsRegion = createSelector(
  [
    selectCallCaptionLines,
    selectCaptionsEnabled,
    selectAreCaptionsBlockedBySignMail,
    (_state: RootState, holdRegionOpen: boolean) => holdRegionOpen,
  ],
  (lines, captionsEnabled, blocked, holdRegionOpen) =>
    captionsEnabled && !blocked && (lines.length > 0 || holdRegionOpen)
);

const selectKnownHearingName = createSelector(
  [primaryHearingSelector],
  (primaryHearing) => {
    const hearingName = primaryHearing?.name?.trim();

    if (!hearingName) {
      return null;
    }

    return UNKNOWN_HEARING_NAME_LABELS.has(hearingName.toLowerCase())
      ? null
      : hearingName;
  }
);

const selectPrimaryHearingPhoneNumber = createSelector(
  [primaryHearingSelector],
  (primaryHearing) => normalizePhoneNumber(primaryHearing?.phoneNumber ?? '')
);

const selectCaptionSpeakerColorMap = createSelector(
  [hearingsSelector],
  (hearings) => buildCaptionSpeakerColorMap(hearings)
);

export const selectResolvedCaptionLines = createSelector(
  [
    selectCallCaptionLines,
    selectKnownHearingName,
    selectPrimaryHearingPhoneNumber,
    selectCaptionSpeakerColorMap,
  ],
  (
    visibleCaptions,
    knownHearingName,
    primaryHearingPhoneNumber,
    speakerColorMap
  ) =>
    visibleCaptions.map((caption) => ({
      ...caption,
      speakerLabel: resolveCaptionSpeakerLabel({
        speakerLabel: caption.speakerLabel,
        speakerRole: caption.speakerRole,
        knownHearingName,
        primaryHearingPhoneNumber,
      }),
      speakerColorVariant: resolveCaptionSpeakerColorVariant({
        speakerRole: caption.speakerRole,
        speakerLabel: caption.speakerLabel,
        speakerColorMap,
      }),
    }))
);

export const makeSelectCaptionTurns = (config: CaptionDisplayConfig) =>
  createSelector(
    [selectResolvedCaptionLines, selectCaptionTrimOffsets],
    (resolvedLines, trimOffsets) => {
      const turns = buildCaptionTurns(
        resolvedLines,
        getCaptionTurnBuildOptions(config)
      );

      return turns.map((turn) => {
        const offset = trimOffsets[turn.key] ?? 0;
        const trimmedText = turn.text.slice(offset).trimStart();

        return {
          ...turn,
          text: trimmedText,
        };
      });
    }
  );
